import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { Badge } from '../../../components/ui/badge';
import { DeactivatableButton } from '../../../components/features/DeactivatableButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  DerogationActionEnumKey,
  derogationActionEnum,
  derogationControllerCheckDerogationRequirementQueryOptions,
  derogationControllerCreateDerogation,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
  Customer,
  FinalizeNewBusinessModuleRule,
  PaymentMethodDtoWithIsPreferred,
  RulePayer,
} from 'korint-sdk';
import { MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';
import { isWaitingForQuoteDerogation } from '../isWaitingForQuoteDerogation';

type CreateDerogationButtonProps = {
  action: DerogationActionEnumKey;
  rules: FinalizeNewBusinessModuleRule[];
  customer: Customer;
  customers: Customer[];
  payers: RulePayer[] | undefined;
  paymentMethods: PaymentMethodDtoWithIsPreferred[];
  hasUnacknowledgedAlerts: boolean;
  overriddenBranchId?: string;
};

export function CreateDerogationButton({
  action,
  rules,
  customer,
  customers,
  payers,
  paymentMethods,
  hasUnacknowledgedAlerts,
  overriddenBranchId,
}: CreateDerogationButtonProps) {
  const { t } = useTranslation();
  const { branches, policyOnMain, ...policyOnDashboard } = usePolicyOnDashboard();
  const finalizeConfig = usePolicyProductConfig('finalizeNewBusinessModuleConfig');

  const branchId = overriddenBranchId ?? policyOnDashboard.workingBranch?.id;
  const branch = branches?.data.find(({ id }) => id === branchId);

  const contractSentLabel =
    finalizeConfig?.buttons?.find(b => b.actionType === 'confirm')?.visual.successMessage ??
    t('finalizeNewBusiness.derogation.sentToWholesaler');

  const { data: policyOnBranch } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyOnMain.id, { branchId: branchId! }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
    enabled: Boolean(branchId),
  });
  const policy = policyOnBranch ?? policyOnDashboard.policy;

  const { data: documents } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id!, { branchId: branchId! }),
    select: data => data.data,
    enabled: Boolean(policy.id && branchId),
  });

  const { mutateAsync: createDerogation, isPending: isCreateDerogationPending } = useMutation({
    mutationFn: () => derogationControllerCreateDerogation(branchId!, { action }),
    onSuccess: () => policyOnDashboard.mutate(),
  });

  const derogations = (branch?.derogations ?? []).filter(
    derogation => derogation.action === action,
  );

  const hasDerogation = derogations?.length > 0;

  const isDerogationCreated = derogations.some(({ status }) => status === 'CREATED');
  const isDerogationApproved = derogations.some(({ status }) => status === 'APPROVED');
  const isDerogationDenied = derogations.some(({ status }) => status === 'DENIED');

  const { data: needsDerogation } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(branchId!, {}, { action }),
    select: data => data.needsDerogation,
    enabled: Boolean(branchId),
  });

  const { data: needsQuoteDerogation } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      branchId!,
      {},
      { action: derogationActionEnum.QUOTE },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(branchId) && action === derogationActionEnum.CONFIRM,
  });

  const derogationValidationRules = [
    {
      isValid: branch?.branchStatus !== 'BRANCH_CLOSED',
      message: t('finalizeNewBusiness.branchClosed'),
    },
    ...rules.map(rule => ({
      isValid:
        payers !== undefined &&
        rule.isValid({
          policy,
          customer,
          customers,
          payers,
          documents: documents ?? [],
          hasUnacknowledgedAlerts,
          paymentMethods,
        }),
      message: t(`finalizeNewBusiness.rules.${rule.id}.message`),
    })),
  ];

  const isHidden =
    !branchId ||
    hasDerogation ||
    !needsDerogation ||
    isWaitingForQuoteDerogation({
      action,
      needsQuoteDerogation,
      derogations: branch?.derogations ?? [],
    });

  return (
    <Group gap={2}>
      <DeactivatableButton
        key="create-derogation"
        onClick={() => createDerogation()}
        variant="ghost"
        className="font-bold text-blue-700"
        icon={<MailIcon className="h-4 w-4" />}
        rules={derogationValidationRules}
        hidden={isHidden}
        isLoading={isCreateDerogationPending}
        info={isHidden ? undefined : t('finalizeNewBusiness.derogation.info')}
      >
        {t('finalizeNewBusiness.derogation.sendToWholesaler')}
      </DeactivatableButton>

      {isDerogationCreated && <Badge variant="outline">{contractSentLabel}</Badge>}

      {isDerogationApproved && (
        <Badge variant="outline">{t('finalizeNewBusiness.derogation.approvedByWholesaler')}</Badge>
      )}

      {isDerogationDenied && (
        <Badge variant="destructive">
          {t('finalizeNewBusiness.derogation.declinedByWholesaler')}
        </Badge>
      )}
    </Group>
  );
}
